import { useState, useEffect } from "react";
import { supabase } from "../lib/SupabaseClient";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

/* ------------------ Types ------------------ */
export interface Choice {
    id?: number;
    text: string;
    is_correct: boolean;
    question_id?: number;
}

export interface Question {
    id?: number;
    text: string;
    type: string;
    bank_id?: number;
    image_url?: string | null;
    choices?: Choice[];
    order_num?: number;
    is_active?: boolean;
    created_utc?: string;
    updated_utc?: string;
    external_id?: string;
}

interface QuestionEditorProProps {
    testId: number;
    question?: Question | null;
    onSaved?: () => void;
}

/* ------------------ Component ------------------ */
export default function QuestionEditorPro({
    testId,
    onSaved,
    question,
}: QuestionEditorProProps) {
    const [text, setText] = useState("");
    const [type, setType] = useState("multiple_choice");
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [bankId, setBankId] = useState<number | null>(null);
    const [choices, setChoices] = useState<Choice[]>([
        { text: "", is_correct: false },
        { text: "", is_correct: false },
        { text: "", is_correct: false },
        { text: "", is_correct: false },
    ]);
    const [banks, setBanks] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    /* ------------------ Prefill for Edit ------------------ */
    useEffect(() => {
        if (question && question.id) {
            setText(question.text || "");
            setType(question.type || "multiple_choice");
            setImageUrl(question.image_url || null);
            setBankId(question.bank_id ?? null);

            if (question.choices && question.choices.length > 0) {
                setChoices(
                    question.choices.map((c) => ({
                        id: c.id,
                        text: c.text || "",
                        is_correct: !!c.is_correct,
                        question_id: c.question_id,
                    }))
                );
            }
        } else {
            resetForm();
        }
    }, [question?.id]);

    /* ------------------ Load Banks ------------------ */
    useEffect(() => {
        async function loadBanks() {
            const { data, error } = await supabase
                .from("question_banks")
                .select("id, name")
                .order("name");
            if (!error && data) setBanks(data);
        }
        loadBanks();
    }, []);

    const resetForm = () => {
        setText("");
        setType("multiple_choice");
        setImageUrl(null);
        setUploadProgress(0);
        setBankId(null);
        setChoices([
            { text: "", is_correct: false },
            { text: "", is_correct: false },
            { text: "", is_correct: false },
            { text: "", is_correct: false },
        ]);
    };

    /* ------------------ Choice Handlers ------------------ */
    const handleChoiceChange = (index: number, field: keyof Choice, value: string | boolean) => {
        setChoices((prev) =>
            prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
        );
    };

    /* ------------------ Image Upload Handler ------------------ */
    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setMessage("Uploading image...");
            setUploadProgress(0);

            // ✅ Use your real bucket name
            const bucketName = "exam-helpers";

            // Use a consistent file naming convention (e.g. question_142.png)
            const fileExt = file.name.split(".").pop();
            const fileName = `question_${question?.id || Date.now()}.${fileExt}`;
            const filePath = `${fileName}`; // direct file, no nested folder unless you want one

            // ✅ Upload with MIME type and upsert enabled
            const { data, error: uploadError } = await supabase.storage
                .from(bucketName)
                .upload(filePath, file, {
                    cacheControl: "3600",
                    upsert: true,
                    contentType: file.type,
                });

            if (uploadError) throw uploadError;

            // ✅ Get public URL
            const { data: publicUrlData } = supabase.storage
                .from(bucketName)
                .getPublicUrl(filePath);

            if (!publicUrlData?.publicUrl) throw new Error("No public URL returned from Supabase.");

            setImageUrl(publicUrlData.publicUrl);
            setMessage("✅ Image uploaded successfully!");
            setUploadProgress(100);

            // ✅ Update DB record if editing an existing question
            if (question?.id) {
                const { error: updateError } = await supabase
                    .from("questions")
                    .update({ image_url: publicUrlData.publicUrl })
                    .eq("id", question.id);
                if (updateError) throw updateError;
            }
        } catch (err: any) {
            console.error("❌ Image upload failed:", err);
            setMessage(`❌ Failed to upload image: ${err.message || err.toString()}`);
        }
    };

    /* ------------------ Save Question ------------------ */
    const handleSave = async () => {
        if (!text.trim()) return alert("Please enter the question text.");
        setLoading(true);
        setMessage("");

        try {
            if (question?.id) {
                const { error: qErr } = await supabase
                    .from("questions")
                    .update({
                        text,
                        type,
                        image_url: imageUrl,
                        updated_utc: new Date().toISOString(),
                    })
                    .eq("id", question.id);
                if (qErr) throw qErr;

                for (const c of choices) {
                    if (c.id) {
                        await supabase
                            .from("choices")
                            .update({ text: c.text, is_correct: c.is_correct })
                            .eq("id", c.id);
                    } else {
                        await supabase
                            .from("choices")
                            .insert([{ text: c.text, is_correct: c.is_correct, question_id: question.id }]);
                    }
                }

                setMessage("✅ Question updated successfully!");
            } else {
                const { data: qData, error: qError } = await supabase
                    .from("questions")
                    .insert([
                        {
                            test_id: testId,
                            bank_id: bankId,
                            text,
                            type,
                            image_url: imageUrl,
                            order_num: 1,
                            is_active: true,
                            created_utc: new Date().toISOString(),
                            updated_utc: new Date().toISOString(),
                            external_id: crypto.randomUUID(),
                        },
                    ])
                    .select("id")
                    .single();

                if (qError) throw qError;

                const { error: cErr } = await supabase.from("choices").insert(
                    choices.map((c, i) => ({
                        question_id: qData.id,
                        text: c.text,
                        is_correct: c.is_correct,
                        order_num: i + 1,
                    }))
                );
                if (cErr) throw cErr;

                setMessage("✅ Question created successfully!");
            }

            onSaved?.();
            resetForm();
        } catch (err: any) {
            console.error("❌ Error saving question:", err.message);
            setMessage(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    /* ------------------ Render ------------------ */
    return (
        <div className="bg-white p-4 rounded shadow">
            <h2 className="text-lg font-bold mb-4 text-indigo-700">
                {question ? "✏️ Edit Question" : "➕ Add New Question"}
            </h2>

            {/* Question Bank */}
            <label className="block mb-2 font-medium text-gray-800">Question Bank</label>
            <select
                value={bankId ?? ""}
                onChange={(e) => setBankId(Number(e.target.value))}
                className="border rounded p-2 mb-4 w-full"
            >
                <option value="">-- Choose Bank --</option>
                {banks.map((b) => (
                    <option key={b.id} value={b.id}>
                        {b.name}
                    </option>
                ))}
            </select>

            {/* Type */}
            <label className="block mb-2 font-medium text-gray-800">Type</label>
            <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="border rounded p-2 mb-4 w-full"
            >
                <option value="multiple_choice">Multiple Choice</option>
                <option value="true_false">True / False</option>
                <option value="short_answer">Short Answer</option>
                <option value="essay">Essay</option>
            </select>

            {/* Question Text */}
            <label className="block mb-2 font-medium text-gray-800">Question Text</label>
            <ReactQuill value={text} onChange={setText} className="mb-4" />

            {/* Attach Image Helper */}
            <div className="mb-6">
                <label className="block font-medium text-gray-800 mb-2">
                    Attach Image Helper
                </label>

                <div className="flex items-center gap-3">
                    <input
                        id="fileInput"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                    />

                    <label
                        htmlFor="fileInput"
                        className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg shadow hover:opacity-90 transition"
                    >
                        📎 Upload Image
                    </label>

                    <span className="text-sm text-gray-600">
                        {imageUrl ? "✅ Image uploaded" : "No file selected"}
                    </span>
                </div>

                {/* Progress bar */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-indigo-600 h-2 rounded-full transition-all"
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                )}

                {/* Preview */}
                {imageUrl && (
                    <div className="mt-3">
                        <img
                            src={imageUrl}
                            alt="Question helper"
                            className="max-h-48 rounded-md border shadow-sm"
                        />
                    </div>
                )}
            </div>

            {/* Choices */}
            {type === "multiple_choice" && (
                <div className="mb-4">
                    <h3 className="font-medium mb-2 text-gray-800">Choices</h3>
                    {choices.map((c, idx) => (
                        <div key={idx} className="flex gap-2 mb-2 items-center">
                            <input
                                type="text"
                                value={c.text}
                                onChange={(e) => handleChoiceChange(idx, "text", e.target.value)}
                                placeholder={`Choice ${idx + 1}`}
                                className="flex-1 border rounded p-2"
                            />
                            <label className="flex items-center gap-1 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={c.is_correct}
                                    onChange={(e) =>
                                        handleChoiceChange(idx, "is_correct", e.target.checked)
                                    }
                                />
                                Correct
                            </label>
                        </div>
                    ))}
                </div>
            )}

            {/* Save */}
            <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
                {loading ? "Saving..." : question ? "Update Question" : "Save Question"}
            </button>

            {message && (
                <p className="mt-4 text-sm text-gray-700 bg-gray-50 border border-gray-200 p-2 rounded">
                    {message}
                </p>
            )}
        </div>
    );
}
